export function asList(value) {
    if (value == null || value === "") {
        return [];
    }
    return Array.isArray(value) ? value : [value];
}

export function normalizeSaleCart(entry) {
    if (entry == null) {
        return null;
    }
    if (typeof entry !== "object") {
        const id = Number(entry);
        return Number.isNaN(id)
            ? null
            : {
                  id,
                  item: "",
                  itemId: null,
                  itemTypeId: null,
                  itemType: "",
              };
    }
    const id = Number(entry.id);
    if (Number.isNaN(id)) {
        return null;
    }
    return {
        id,
        item: entry.item || entry.name || "",
        itemId: entry.item_id ?? entry.itemId ?? null,
        itemTypeId: entry.item_type_id ?? entry.itemTypeId ?? null,
        itemType: entry.item_type ?? entry.itemType ?? "",
    };
}

export function normalizeSaleCarts(value) {
    return asList(value).map(normalizeSaleCart).filter(Boolean);
}

export function groupSaleCartsByItemType(carts) {
    const groups = new Map();
    normalizeSaleCarts(carts).forEach((cart) => {
        const key = cart.itemTypeId == null ? "none" : String(cart.itemTypeId);
        if (!groups.has(key)) {
            groups.set(key, {
                id: cart.itemTypeId,
                name: cart.itemType || "",
                carts: [],
            });
        }
        groups.get(key).carts.push(cart);
    });
    return Array.from(groups.values());
}

export function resolveSaleCartIds(kind, selectedId, carts) {
    const normalized = normalizeSaleCarts(carts);
    if (selectedId == null || selectedId === "") {
        return [];
    }
    if (kind === "itemType") {
        return normalized
            .filter((cart) => String(cart.itemTypeId) === String(selectedId))
            .map((cart) => cart.id);
    }
    const match = normalized.find(
        (cart) =>
            String(cart.id) === String(selectedId) ||
            String(cart.itemId) === String(selectedId)
    );
    return match ? [match.id] : [];
}

export function inferTarget(saleCarts, allCarts) {
    const selected = normalizeSaleCarts(saleCarts);
    if (!selected.length) {
        return { kind: "item", id: null, saleCartIds: [] };
    }
    const typeIds = [
        ...new Set(
            selected
                .map((cart) => cart.itemTypeId)
                .filter((value) => value != null)
        ),
    ];
    if (typeIds.length === 1) {
        const typeId = typeIds[0];
        const allOfType = normalizeSaleCarts(allCarts).filter(
            (cart) => String(cart.itemTypeId) === String(typeId)
        );
        const selectedIds = new Set(selected.map((cart) => cart.id));
        const coversType =
            allOfType.length > 1 &&
            allOfType.every((cart) => selectedIds.has(cart.id));
        if (coversType) {
            return {
                kind: "itemType",
                id: typeId,
                saleCartIds: allOfType.map((cart) => cart.id),
            };
        }
    }
    return {
        kind: "item",
        id: selected[0].itemId || selected[0].id,
        saleCartIds: [selected[0].id],
    };
}

export function describeTarget(saleCarts, t) {
    const carts = normalizeSaleCarts(saleCarts);
    if (!carts.length) {
        return t("offers.target.unassigned");
    }
    const typeNames = [
        ...new Set(carts.map((cart) => cart.itemType).filter(Boolean)),
    ];
    if (carts.length > 1 && typeNames.length === 1) {
        return t("offers.target.typeWithCount", {
            name: typeNames[0],
            count: carts.length,
        });
    }
    if (carts.length === 1) {
        return carts[0].item || t("offers.target.item");
    }
    return t("offers.target.itemCount", { count: carts.length });
}

export function formatDiscount(amount, discountType) {
    const value = Number(amount) || 0;
    return Number(discountType) === 2 ? String(value) : `${value}%`;
}

export function remainingLabel(remain, total) {
    const safeRemain = remain === -1 || remain == null ? 0 : Number(remain);
    const safeTotal = total === -1 || total == null ? 0 : Number(total);
    return { remain: safeRemain, total: safeTotal };
}

export function isOfferExpired(dateEnd, now = new Date()) {
    if (!dateEnd) {
        return false;
    }
    const end = new Date(dateEnd);
    if (Number.isNaN(end.getTime())) {
        return false;
    }
    return end < now;
}

export function matchesOfferFilter(offer, { query, scope }) {
    const haystack = [
        offer?.name,
        offer?.description,
        describeTarget(offer?.sale_cart, (key) => key),
        ...normalizeSaleCarts(offer?.sale_cart).flatMap((cart) => [
            cart.item,
            cart.itemType,
        ]),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    if (query && !haystack.includes(query.trim().toLowerCase())) {
        return false;
    }
    if (scope === "itemType") {
        return normalizeSaleCarts(offer?.sale_cart).length > 1;
    }
    if (scope === "item") {
        return normalizeSaleCarts(offer?.sale_cart).length === 1;
    }
    return true;
}
